import React, { useState, useMemo } from "react";
import ExportButtons from "./ExportButtons";

const MonthlyBoxingTableModal = ({ data = {}, onClose }) => {
  const orderTotals = data.order_total_pieces || {};
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const filtered = useMemo(() => {
    return (data.boxing_logs || []).filter((row) => {
      const logDate = new Date(row.date);
      return logDate.getMonth() === currentMonth && logDate.getFullYear() === currentYear;
    });
  }, [data]);

  const grouped = {};
  const completedByOrder = {};

  filtered.forEach((entry) => {
    const order = entry.cut_order_no || `Order-${entry.order_id}`;
    const line = entry.line || entry.box_no || "No Box No";
    const colorNames = entry.suborder_colour_name || (
      entry.suborder_colors?.map(c => c.suborder_colour_name).join(", ") || "Color"
    );
    const normalizedColor = colorNames.trim().toLowerCase();

    if (!grouped[order]) grouped[order] = {};
    if (!grouped[order][line]) grouped[order][line] = {};
    if (!grouped[order][line][normalizedColor]) grouped[order][line][normalizedColor] = [];

    grouped[order][line][normalizedColor].push(entry);

    if (!completedByOrder[order]) completedByOrder[order] = {};
    if (!completedByOrder[order][normalizedColor]) completedByOrder[order][normalizedColor] = 0;

    completedByOrder[order][normalizedColor] += entry.total_piece;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white w-full h-full p-6 overflow-y-auto relative rounded-none shadow-none">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500 z-50"
          onClick={onClose}
        >
          ✖
        </button>

        <div className="flex justify-between items-center mb-6 pr-10">
          <h2 className="text-3xl font-bold text-indigo-800">📦 Boxing - Monthly Report</h2>
          <ExportButtons data={filtered} filename="Monthly_Boxing_Report" elementId="monthly-boxing-table" />
        </div>

        <div id="monthly-boxing-table" className="px-4">
          {Object.entries(orderTotals).map(([order, colorsPlanned]) => {
            const orderCompletedByColor = completedByOrder[order] || {};
            const orderTotalPlanned = Object.values(colorsPlanned).reduce((a, b) => a + b, 0);
            const orderTotalCompleted = Object.values(orderCompletedByColor).reduce((a, b) => a + b, 0);
            const orderTotalPending = Math.max(orderTotalPlanned - orderTotalCompleted, 0);

            return (
              <div key={order} className="mb-10 border-t border-gray-300 pt-4">
                <h3 className="text-lg font-bold text-white bg-indigo-600 px-4 py-2 rounded-md shadow-md">
                  Order No: {order}
                </h3>
                <div className="text-sm text-gray-800 px-4 py-2 bg-gray-50 border rounded-b mb-4">
                  📦 <strong>Order Quantity:</strong> {orderTotalPlanned} &nbsp; | &nbsp;
                  ✅ <strong>Completed:</strong> {orderTotalCompleted} &nbsp; | &nbsp;
                  ⏳ <strong>Pending:</strong> {orderTotalPending}
                </div>

                {Object.entries(colorsPlanned).map(([color, planned]) => {
                  const normalizedColor = color.trim().toLowerCase();
                  const colorCompleted = orderCompletedByColor[normalizedColor] || 0;
                  const pending = Math.max(planned - colorCompleted, 0);

                  const lines = grouped[order] || {};
                  const entriesByLine = Object.entries(lines).reduce((acc, [line, colorGroups]) => {
                    if (colorGroups[normalizedColor]) acc.push({ line, entries: colorGroups[normalizedColor] });
                    return acc;
                  }, []);

                  return (
                    <div key={color} className="mb-6 border border-gray-200 rounded-lg shadow bg-white">
                      <div className="bg-indigo-100 px-4 py-2 rounded-t-md flex flex-wrap justify-between">
                        <div className="text-sm font-semibold text-indigo-900">
                          🎨 Color: <span className="bg-white px-2 py-1 rounded text-indigo-700 font-medium">{color}</span>
                        </div>
                        <div className="text-sm text-gray-700 flex gap-4">
                          <span>✅ Completed: <strong>{colorCompleted}</strong></span>
                          <span>📦 Order Quantity: <strong>{planned}</strong></span>
                          <span>⏳ Pending: <strong>{pending}</strong></span>
                        </div>
                      </div>

                      <table className="w-full text-sm">
                        <thead className="bg-indigo-200 text-indigo-900 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Job No</th>
                            <th className="px-4 py-2 border">Box No</th>
                            <th className="px-4 py-2 border">Total Pieces</th>
                            <th className="px-4 py-2 border">Sizes</th>
                            <th className="px-4 py-2 border">Request Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entriesByLine.length > 0 ? (
                            entriesByLine.flatMap(({ line, entries }, idx) =>
                              entries.map((row, i) => (
                                <tr key={`${idx}-${i}`} className="hover:bg-indigo-50 transition-all">
                                  <td className="px-4 py-2 border text-gray-800">{row.job_no}</td>
                                  <td className="px-4 py-2 border text-gray-800">{line}</td>
                                  <td className="px-4 py-2 border text-center font-bold text-green-700">{row.total_piece}</td>
                                  <td className="px-4 py-2 border text-gray-700">
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(row.size_variant_quantity || {}).map(([size, qty]) => (
                                        <span
                                          key={size}
                                          className="bg-yellow-100 text-gray-800 text-xs px-2 py-1 rounded shadow-sm"
                                        >
                                          {size}: {qty}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 border text-gray-600 italic">
                                    {row.request_status || "-"}
                                  </td>
                                </tr>
                              ))
                            )
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-3 italic text-gray-500">
                                No boxing entries for this color in the month.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <p className="text-center text-gray-500 mt-6 italic">
              No boxing data available for the current month.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyBoxingTableModal;
